import type { LocalizedCopy } from "../productCopyTypes";

/** Rich HTML product copy for 97032 — headings, lists, specs in content. */
export const RICH_PRODUCT_COPY_97032: LocalizedCopy = {
  en: `<h1>12GO Type 99B Main Battle Tank 1:28 - 97032</h1>

<p>
Experience one of China's most advanced modern battle tanks in brick-built form.
The 12GO Type 99B Main Battle Tank is a highly detailed construction model inspired by the real-life Type 99B,
a frontline main battle tank used by the Chinese People's Liberation Army.
</p>

<p>
Designed in an impressive 1:28 scale, this model combines realistic proportions, functional features,
and a richly detailed interior, making it an excellent choice for military enthusiasts, collectors,
and fans of advanced building sets alike.
</p>

<p>
With its desert digital camouflage, aggressive silhouette, and extensive detailing,
the finished model delivers a striking display piece while also offering an engaging
and rewarding building experience.
</p>

<h2>Product Features</h2>

<ul>
  <li><strong>Brand:</strong> 12GO</li>
  <li><strong>Model:</strong> Type 99B Main Battle Tank</li>
  <li><strong>Item Number:</strong> 97032</li>
  <li><strong>Scale:</strong> 1:28</li>
  <li><strong>Piece Count:</strong> 1916+ pieces</li>
  <li><strong>Model Length:</strong> approx. 39.6 cm</li>
  <li><strong>Width:</strong> approx. 12.5 cm</li>
  <li><strong>Height:</strong> approx. 16 cm</li>
  <li><strong>Recommended Age:</strong> 14+</li>
</ul>

<h2>Functional Details</h2>

<p>
The model includes a variety of interactive and movable elements:
</p>

<ul>
  <li>rotating turret</li>
  <li>adjustable main cannon elevation</li>
  <li>movable machine gun</li>
  <li>removable hull and turret panels</li>
  <li>detailed interior sections</li>
  <li>realistic tracked suspension design</li>
  <li>rear-mounted external equipment details</li>
</ul>

<p>
One of the standout features of this set is its partially accessible interior.
The designers recreated internal compartments and mechanical details to give the model
a more authentic and technical appearance.
</p>

<h2>About the Real Type 99B Tank</h2>

<p>
The Type 99B is an upgraded version of the Chinese Type 99 main battle tank,
originally introduced in the late 1990s. Developed as China's answer to modern Western tanks
such as the M1 Abrams and Leopard 2, the Type 99 series represents the country's top-tier armored warfare technology.
</p>

<p>
The improved Type 99B variant features enhanced composite armor, upgraded fire-control systems,
advanced optics, and a powerful high-performance engine that provides excellent battlefield mobility.
Its distinctive turret shape and modular armor configuration make it one of the most recognizable
modern Chinese armored vehicles.
</p>

<p>
Today, the Type 99B serves with elite armored units of the People's Liberation Army and is frequently
showcased during military parades and large-scale exercises as a symbol of China's modern armored capabilities.
</p>

<h2>A Perfect Set for Military Model Fans</h2>

<p>
The 12GO Type 99B is an ideal choice for:
</p>

<ul>
  <li>military vehicle collectors</li>
  <li>fans of modern armored warfare</li>
  <li>advanced brick-building enthusiasts</li>
  <li>display-focused model builders</li>
  <li>anyone looking for a large and immersive construction project</li>
</ul>

<p>
Once completed, the model becomes an impressive display centerpiece thanks to its size,
realistic proportions, and modern military styling. It is a combination of technical building,
authentic design, and contemporary armored vehicle engineering.
</p>`,

  pl: `<h1>12GO Type 99B — czołg podstawowy 1:28 — 97032</h1>

<p>
Jeden z najnowocześniejszych chińskich czołgów podstawowych w formie modelu do złożenia.
Type 99B 12GO wiernie nawiązuje do rzeczywistego Type 99B — czołgu pierwszej linii Ludowej Armii Wyzwoleńczej.
</p>

<p>
W skali 1:28 łączy proporcje zbliżone do oryginału, elementy ruchome i dopracowane wnętrze.
Sprawdzi się u miłośników militariów, kolekcjonerów i zaawansowanych budowniczych.
</p>

<p>
Pustynny kamuflaż cyfrowy, ostra sylwetka i liczne detale sprawiają, że gotowy model dobrze wygląda na ekspozycji,
a sama budowa daje satysfakcję.
</p>

<h2>Cechy zestawu</h2>

<ul>
  <li><strong>Marka:</strong> 12GO</li>
  <li><strong>Model:</strong> Type 99B Main Battle Tank</li>
  <li><strong>Numer katalogowy:</strong> 97032</li>
  <li><strong>Skala:</strong> 1:28</li>
  <li><strong>Liczba elementów:</strong> 1916+</li>
  <li><strong>Długość modelu:</strong> ok. 39,6 cm</li>
  <li><strong>Szerokość:</strong> ok. 12,5 cm</li>
  <li><strong>Wysokość:</strong> ok. 16 cm</li>
  <li><strong>Wiek zalecany:</strong> 14+</li>
</ul>

<h2>Elementy ruchome i funkcje</h2>

<p>
Model oferuje szereg interaktywnych rozwiązań:
</p>

<ul>
  <li>obracana wieża</li>
  <li>regulowany kąt podniesienia armaty głównej</li>
  <li>ruchomy karabin maszynowy</li>
  <li>zdejmowane panele kadłuba i wieży</li>
  <li>szczegółowe wnętrze</li>
  <li>napęd gąsienicowy z zawieszeniem</li>
  <li>sprzęt zewnętrzny na tylnej płycie kadłuba</li>
</ul>

<p>
Wyróżnikiem jest częściowo dostępne wnętrze — przegrody i detale mechaniczne
nadają modelowi bardziej techniczny, zbliżony do oryginału charakter.
</p>

<h2>Prawdziwy Type 99B</h2>

<p>
Type 99B to rozwinięcie chińskiego Type 99 wprowadzonego pod koniec lat 90. XX wieku —
odpowiedź na zachodnie konstrukcje w rodzaju M1 Abrams czy Leopard 2
i jeden z najważniejszych czołgów w arsenale ChRL.
</p>

<p>
Wersja B ma ulepszony pancerz kompozytowy, nowsze systemy prowadzenia ognia i optoelektronikę
oraz silnik o wysokiej mocy, co zapewnia dobrą mobilność w boju.
Charakterystyczna wieża i modułowa konfiguracja pancerza czynią go jednym z najbardziej rozpoznawalnych
współczesnych chińskich pojazdów opancerzonych.
</p>

<p>
Type 99B służy w elitarnych jednostkach pancernych i często pojawia się na paradach
oraz dużych ćwiczeniach jako symbol nowoczesnych zdolności pancernych ChRL.
</p>

<h2>Dla kogo jest ten zestaw</h2>

<p>
Type 99B 12GO to dobry wybór, jeśli interesują Cię:
</p>

<ul>
  <li>kolekcjonowanie pojazdów wojskowych</li>
  <li>współczesny pancerz</li>
  <li>zaawansowane projekty konstrukcyjne</li>
  <li>modele pod ekspozycję</li>
  <li>duże, wciągające budowy</li>
</ul>

<p>
Po złożeniu model robi wrażenie gabarytem, proporcjami i nowoczesną stylistyką wojskową —
łączy w sobie techniczną budowę, wierny design i współczesną inżynierię pojazdów opancerzonych.
</p>`,

  de: `<h1>12GO Type 99B Kampfpanzer 1:28 – 97032</h1>

<p>
Einer der modernsten chinesischen Kampfpanzer als detailreiches Klemmbausteinmodell.
Der 12GO Type 99B orientiert sich am echten Type 99B — Gefechtspanzer der Volksbefreiungsarmee an der Front.
</p>

<p>
Im Maßstab 1:28 verbindet er realistische Proportionen, bewegliche Details und ein ausgearbeitetes Innenleben —
für Militärfans, Sammler und erfahrene Bauherren.
</p>

<p>
Wüsten-Digitaltarnung, aggressive Silhouette und feine Oberflächendetails machen das fertige Modell
zu einem eindrucksvollen Ausstellungsstück und einem lohnenden Projekt.
</p>

<h2>Produkteigenschaften</h2>

<ul>
  <li><strong>Marke:</strong> 12GO</li>
  <li><strong>Modell:</strong> Type 99B Main Battle Tank</li>
  <li><strong>Artikelnummer:</strong> 97032</li>
  <li><strong>Maßstab:</strong> 1:28</li>
  <li><strong>Teileanzahl:</strong> 1916+</li>
  <li><strong>Modelllänge:</strong> ca. 39,6 cm</li>
  <li><strong>Breite:</strong> ca. 12,5 cm</li>
  <li><strong>Höhe:</strong> ca. 16 cm</li>
  <li><strong>Empfohlenes Alter:</strong> 14+</li>
</ul>

<h2>Funktionen und bewegliche Teile</h2>

<p>Das Modell bietet zahlreiche interaktive Elemente:</p>

<ul>
  <li>drehbarer Turm</li>
  <li>verstellbare Hauptkanonen-Elevation</li>
  <li>bewegliches Maschinengewehr</li>
  <li>abnehmbare Rumpf- und Turmpaneele</li>
  <li>detaillierte Innenräume</li>
  <li>realistische Kettenaufhängung</li>
  <li>Heckausrüstung</li>
</ul>

<p>Teilweise zugängliche Innenräume verleihen dem Modell technische Tiefe und Authentizität.</p>

<h2>Der echte Type 99B</h2>

<p>
Der Type 99B ist eine Weiterentwicklung des chinesischen Type 99 (Ende der 1990er) —
Chinas Antwort auf Abrams und Leopard 2 und Spitze der Panzertechnologie.
</p>

<p>
Die B-Variante bringt verbesserte Verbundpanzerung, modernisierte Feuerleitung und Optik
sowie einen leistungsstarken Motor für hohe Geländegängigkeit.
</p>

<p>
Heute dient er in elitären PLA-Panzereinheiten und erscheint häufig bei Paraden und Großmanövern.
</p>

<h2>Für wen geeignet</h2>

<ul>
  <li>Sammler militärischer Fahrzeuge</li>
  <li>Freunde moderner Panzerung</li>
  <li>erfahrene Klemmsteinbauer</li>
  <li>Ausstellungsmodelle</li>
  <li>große, intensive Bauprojekte</li>
</ul>

<p>
Fertig wirkt das Modell durch Größe, Proportionen und modernes Militärdesign wie ein starker Mittelpunkt jeder Vitrine.
</p>`,

  es: `<h1>12GO Type 99B tanque principal 1:28 - 97032</h1>

<p>
Uno de los carros de combate modernos más avanzados de China en forma de maqueta detallada.
El Type 99B de 12GO reproduce el Type 99B real, MBT de primera línea del Ejército Popular de Liberación.
</p>

<p>
A escala 1:28 combina proporciones creíbles, funciones móviles e interior trabajado — ideal para aficionados militares, coleccionistas y constructores avanzados.
</p>

<p>
Camuflaje digital desértico, silueta agresiva y abundante detalle convierten el modelo terminado en pieza de exposición llamativa y montaje gratificante.
</p>

<h2>Características del producto</h2>

<ul>
  <li><strong>Marca:</strong> 12GO</li>
  <li><strong>Modelo:</strong> Type 99B Main Battle Tank</li>
  <li><strong>Referencia:</strong> 97032</li>
  <li><strong>Escala:</strong> 1:28</li>
  <li><strong>Piezas:</strong> 1916+</li>
  <li><strong>Longitud:</strong> aprox. 39,6 cm</li>
  <li><strong>Ancho:</strong> aprox. 12,5 cm</li>
  <li><strong>Altura:</strong> aprox. 16 cm</li>
  <li><strong>Edad recomendada:</strong> 14+</li>
</ul>

<h2>Detalles funcionales</h2>

<p>El modelo incluye elementos interactivos:</p>

<ul>
  <li>torreta giratoria</li>
  <li>elevación del cañón principal</li>
  <li>ametralladora móvil</li>
  <li>paneles extraíbles del casco y torreta</li>
  <li>interior detallado</li>
  <li>suspensión de orugas realista</li>
  <li>equipo exterior trasero</li>
</ul>

<p>El interior parcialmente accesible aporta profundidad técnica y autenticidad.</p>

<h2>Sobre el Type 99B real</h2>

<p>
El Type 99B evoluciona el Type 99 chino de finales de los noventa, respuesta a tanques occidentales como el M1 Abrams y el Leopard 2.
</p>

<p>
La variante B añade blindaje compuesto mejorado, control de fuego y óptica actualizados, y un motor potente para buena movilidad en combate.
</p>

<p>
Hoy sirve en unidades blindadas de élite del EPLA y aparece a menudo en desfiles y grandes ejercicios.
</p>

<h2>Ideal para</h2>

<ul>
  <li>coleccionistas de vehículos militares</li>
  <li>aficionados al blindaje moderno</li>
  <li>constructores avanzados</li>
  <li>modelos de exposición</li>
  <li>proyectos grandes e inmersivos</li>
</ul>

<p>
Terminado, el modelo impresiona por tamaño, proporciones y estilo militar contemporáneo.
</p>`,

  fr: `<h1>12GO Type 99B char de combat 1:28 - 97032</h1>

<p>
L'un des chars de combat modernes les plus avancés de Chine en maquette détaillée.
Le Type 99B 12GO s'inspire du Type 99B réel, char de première ligne de l'Armée populaire de libération.
</p>

<p>
À l'échelle 1:28, il allie proportions réalistes, fonctions mobiles et intérieur travaillé — pour passionnés militaires, collectionneurs et bâtisseurs confirmés.
</p>

<p>
Camouflage numérique désert, silhouette agressive et riches détails de surface en font une pièce d'exposition saisissante et un montage gratifiant.
</p>

<h2>Caractéristiques</h2>

<ul>
  <li><strong>Marque :</strong> 12GO</li>
  <li><strong>Modèle :</strong> Type 99B Main Battle Tank</li>
  <li><strong>Référence :</strong> 97032</li>
  <li><strong>Échelle :</strong> 1:28</li>
  <li><strong>Pièces :</strong> 1916+</li>
  <li><strong>Longueur :</strong> env. 39,6 cm</li>
  <li><strong>Largeur :</strong> env. 12,5 cm</li>
  <li><strong>Hauteur :</strong> env. 16 cm</li>
  <li><strong>Âge recommandé :</strong> 14+</li>
</ul>

<h2>Détails fonctionnels</h2>

<p>Le modèle propose de nombreux éléments interactifs :</p>

<ul>
  <li>tourelle rotative</li>
  <li>canon principal en élévation</li>
  <li>mitrailleuse mobile</li>
  <li>panneaux de coque et de tourelle amovibles</li>
  <li>intérieur détaillé</li>
  <li>suspension à chenilles réaliste</li>
  <li>équipements arrière</li>
</ul>

<p>L'intérieur partiellement accessible renforce le côté technique et authentique.</p>

<h2>Le vrai Type 99B</h2>

<p>
Le Type 99B prolonge le Type 99 chinois des années 1990, réponse aux Abrams et Leopard 2 occidentaux.
</p>

<p>
La variante B apporte blindage composite renforcé, conduite de tir et optique modernisées, et un moteur puissant pour une bonne mobilité au combat.
</p>

<p>
Il sert aujourd'hui dans des unités blindées d'élite de l'APL et figure souvent aux défilés et grands exercices.
</p>

<h2>Pour qui</h2>

<ul>
  <li>collectionneurs de véhicules militaires</li>
  <li>amateurs d'armure moderne</li>
  <li>bâtisseurs expérimentés</li>
  <li>modèles d'exposition</li>
  <li>grands projets immersifs</li>
</ul>

<p>
Une fois terminé, le modèle impressionne par sa taille, ses proportions et son style militaire contemporain.
</p>`,

  nl: `<h1>12GO Type 99B gevechtstank 1:28 - 97032</h1>

<p>
Een van de modernste Chinese gevechtstanks in gedetailleerd steenmodel.
De 12GO Type 99B volgt de echte Type 99B — een frontlinie-MBT van het Volksbevrijdingsleger.
</p>

<p>
Op schaal 1:28 combineert hij realistische verhoudingen, bewegende details en een uitgewerkt interieur — geschikt voor militaire liefhebbers, verzamelaars en gevorderde bouwers.
</p>

<p>
Woestijndigitalcamouflage, agressief silhouet en veel oppervlaktedetail maken het af model tot een opvallend pronkstuk en een belonend bouwproject.
</p>

<h2>Productkenmerken</h2>

<ul>
  <li><strong>Merk:</strong> 12GO</li>
  <li><strong>Model:</strong> Type 99B Main Battle Tank</li>
  <li><strong>Artikelnummer:</strong> 97032</li>
  <li><strong>Schaal:</strong> 1:28</li>
  <li><strong>Onderdelen:</strong> 1916+</li>
  <li><strong>Lengte:</strong> ca. 39,6 cm</li>
  <li><strong>Breedte:</strong> ca. 12,5 cm</li>
  <li><strong>Hoogte:</strong> ca. 16 cm</li>
  <li><strong>Aanbevolen leeftijd:</strong> 14+</li>
</ul>

<h2>Functionele details</h2>

<p>Het model biedt interactieve elementen:</p>

<ul>
  <li>draaiende toren</li>
  <li>verstelbare hoofdkanonhoogte</li>
  <li>beweegbaar mitrailleur</li>
  <li>verwijderbare romp- en torenpanelen</li>
  <li>gedetailleerd interieur</li>
  <li>realistische rupsophanging</li>
  <li>achteruitrusting</li>
</ul>

<p>Deels toegankelijke compartimenten geven technische diepte en authenticiteit.</p>

<h2>De echte Type 99B</h2>

<p>
De Type 99B is een verbeterde variant van de Chinese Type 99 uit eind jaren negentig — antwoord op Abrams en Leopard 2.
</p>

<p>
De B-versie heeft beter composietpantser, vernieuwde vuurleiding en optiek, en een krachtige motor voor goede mobiliteit in het veld.
</p>

<p>
Hij dient bij elite-eenheden van het VBL en verschijnt regelmatig bij parades en grote oefeningen.
</p>

<h2>Geschikt voor</h2>

<ul>
  <li>verzamelaars van militaire voertuigen</li>
  <li>fans van moderne pantsering</li>
  <li>gevorderde bouwers</li>
  <li>presentatiemodellen</li>
  <li>grote, meeslepende projecten</li>
</ul>

<p>
Afgewerkt maakt het model indruk door formaat, verhoudingen en moderne militaire styling.
</p>`,

  zh: `<h1>12GO Type 99B 主战坦克 1:28 - 97032</h1>

<p>
以积木形式呈现中国最先进的主战坦克之一。12GO Type 99B 高度还原解放军一线装备的实装 99B。
</p>

<p>
1:28 比例兼顾外形准确度、可动结构与丰富内构，适合军事爱好者、收藏者与进阶玩家。
</p>

<p>
沙漠数码迷彩、凌厉轮廓与大量外观细节，使成品既是醒目陈列，也是富有成就感的搭建体验。
</p>

<h2>产品特点</h2>

<ul>
  <li><strong>品牌：</strong> 12GO</li>
  <li><strong>型号：</strong> Type 99B Main Battle Tank</li>
  <li><strong>货号：</strong> 97032</li>
  <li><strong>比例：</strong> 1:28</li>
  <li><strong>颗粒数：</strong> 1916+</li>
  <li><strong>成品长度：</strong> 约 39.6 cm</li>
  <li><strong>宽度：</strong> 约 12.5 cm</li>
  <li><strong>高度：</strong> 约 16 cm</li>
  <li><strong>建议年龄：</strong> 14+</li>
</ul>

<h2>可动与功能细节</h2>

<p>模型包含多种互动结构：</p>

<ul>
  <li>炮塔可旋转</li>
  <li>主炮可俯仰</li>
  <li>机枪可动</li>
  <li>车体与炮塔面板可拆</li>
  <li>细致内构</li>
  <li>履带悬挂</li>
  <li>车尾外挂装备</li>
</ul>

<p>部分可探入的内构强化机械感与还原度。</p>

<h2>关于实装 99B</h2>

<p>
99B 型是中国 99 式主战坦克的升级型，99 式于上世纪末列装，被视为对 M1 艾布拉姆斯、豹 2 等西方主战坦克的回应。
</p>

<p>
99B 强化复合装甲、火控与光电，并以高性能发动机保障战场机动。
</p>

<p>
今日 99B 服役于解放军精锐装甲部队，常出现在阅兵与大规模演习中。
</p>

<h2>适合人群</h2>

<ul>
  <li>军模收藏者</li>
  <li>现代装甲题材爱好者</li>
  <li>进阶积木玩家</li>
  <li>陈列向作品</li>
  <li>大型沉浸式工程</li>
</ul>

<p>
完成后凭借尺寸、比例与现代军用造型，成为陈列中的焦点。
</p>`,
  it: "<h1>Carro armato da combattimento principale 12GO Type 99B 1:28 - 97032</h1>\n\n<p>\nScopri uno dei più avanzati carri armati da combattimento moderni della Cina, ricreato con mattoncini.\nIl carro armato da combattimento principale 12GO Type 99B è un modello da costruzione altamente dettagliato, ispirato al Type 99B reale,\nun carro armato da combattimento principale di prima linea utilizzato dall'Esercito Popolare di Liberazione cinese.\n</p>\n\n<p>\nProgettato nell'impressionante scala 1:28, questo modello combina proporzioni realistiche, caratteristiche funzionali\ne un interno riccamente dettagliato, rendendolo una scelta eccellente per gli appassionati di ambito militare, i collezionisti\ne gli amanti dei set da costruzione avanzati.\n</p>\n\n<p>\nCon la sua mimetica digitale desertica, la silhouette aggressiva e gli ampi dettagli,\nil modello finito offre un elemento da esposizione di grande impatto, oltre a un'esperienza di costruzione coinvolgente\ne gratificante.\n</p>\n\n<h2>Caratteristiche del prodotto</h2>\n\n<ul>\n  <li><strong>Marca:</strong> 12GO</li>\n  <li><strong>Modello:</strong> Carro armato da combattimento principale Type 99B</li>\n  <li><strong>Numero articolo:</strong> 97032</li>\n  <li><strong>Scala:</strong> 1:28</li>\n  <li><strong>Numero di pezzi:</strong> oltre 1916 pezzi</li>\n  <li><strong>Lunghezza del modello:</strong> circa 39,6 cm</li>\n  <li><strong>Larghezza:</strong> circa 12,5 cm</li>\n  <li><strong>Altezza:</strong> circa 16 cm</li>\n  <li><strong>Età consigliata:</strong> 14+</li>\n</ul>\n\n<h2>Dettagli funzionali</h2>\n\n<p>\nIl modello include una varietà di elementi interattivi e mobili:\n</p>\n\n<ul>\n  <li>torretta rotante</li>\n  <li>elevazione regolabile del cannone principale</li>\n  <li>mitragliatrice mobile</li>\n  <li>pannelli rimovibili dello scafo e della torretta</li>\n  <li>sezioni interne dettagliate</li>\n  <li>design realistico della sospensione dei cingoli</li>\n  <li>dettagli delle attrezzature esterne montate posteriormente</li>\n</ul>\n\n<p>\nUna delle caratteristiche distintive di questo set è il suo interno parzialmente accessibile.\nI designer hanno ricreato i compartimenti interni e i dettagli meccanici per conferire al modello\nun aspetto più autentico e tecnico.\n</p>\n\n<h2>Informazioni sul vero carro armato Type 99B</h2>\n\n<p>\nIl Type 99B è una versione migliorata del carro armato da combattimento principale cinese Type 99,\nintrodotto originariamente alla fine degli anni Novanta. Sviluppata come risposta cinese ai moderni carri armati occidentali,\ncome l'M1 Abrams e il Leopard 2, la serie Type 99 rappresenta la tecnologia di punta del Paese nel settore della guerra corazzata.\n</p>\n\n<p>\nLa variante migliorata Type 99B presenta una corazza composita potenziata, sistemi di controllo del tiro aggiornati,\notiche avanzate e un potente motore ad alte prestazioni che garantisce un'eccellente mobilità sul campo di battaglia.\nLa caratteristica forma della torretta e la configurazione modulare della corazza lo rendono uno dei veicoli corazzati cinesi\nmoderni più riconoscibili.\n</p>\n\n<p>\nOggi, il Type 99B è in servizio presso le unità corazzate d'élite dell'Esercito Popolare di Liberazione e viene spesso\npresentato durante parate militari ed esercitazioni su larga scala come simbolo delle moderne capacità corazzate della Cina.\n</p>\n\n<h2>Il set perfetto per gli appassionati di modellismo militare</h2>\n\n<p>\nIl 12GO Type 99B è la scelta ideale per:\n</p>\n\n<ul>\n  <li>collezionisti di veicoli militari</li>\n  <li>appassionati di guerra corazzata moderna</li>\n  <li>appassionati esperti di costruzioni con mattoncini</li>\n  <li>modellisti che danno priorità all'esposizione</li>\n  <li>chiunque cerchi un progetto di costruzione ampio e coinvolgente</li>\n</ul>\n\n<p>\nUna volta completato, il modello diventa un impressionante elemento centrale da esposizione grazie alle sue dimensioni,\nalle proporzioni realistiche e allo stile militare moderno. È una combinazione di costruzione tecnica,\ndesign autentico e ingegneria contemporanea dei veicoli corazzati.\n</p>",
  ru: "<h1>Основной боевой танк 12GO Type 99B 1:28 — 97032</h1>\n\n<p>\nОткройте для себя один из самых передовых современных боевых танков Китая, выполненный из конструктора.\nОсновной боевой танк 12GO Type 99B — это высокодетализированная сборная модель, вдохновлённая реальным Type 99B,\nосновным боевым танком первой линии, используемым Народно-освободительной армией Китая.\n</p>\n\n<p>\nСозданная во впечатляющем масштабе 1:28, эта модель сочетает реалистичные пропорции, функциональные элементы\nи богато детализированный интерьер, что делает её отличным выбором для любителей военной техники, коллекционеров\nи поклонников продвинутых конструкторов.\n</p>\n\n<p>\nБлагодаря пустынному цифровому камуфляжу, агрессивному силуэту и множеству деталей\nготовая модель станет эффектным экспонатом, а процесс сборки подарит увлекательный\nи приятный опыт.\n</p>\n\n<h2>Особенности товара</h2>\n\n<ul>\n  <li><strong>Бренд:</strong> 12GO</li>\n  <li><strong>Модель:</strong> основной боевой танк Type 99B</li>\n  <li><strong>Артикул:</strong> 97032</li>\n  <li><strong>Масштаб:</strong> 1:28</li>\n  <li><strong>Количество деталей:</strong> более 1916 деталей</li>\n  <li><strong>Длина модели:</strong> около 39,6 см</li>\n  <li><strong>Ширина:</strong> около 12,5 см</li>\n  <li><strong>Высота:</strong> около 16 см</li>\n  <li><strong>Рекомендуемый возраст:</strong> 14+</li>\n</ul>\n\n<h2>Функциональные детали</h2>\n\n<p>\nМодель включает множество интерактивных и подвижных элементов:\n</p>\n\n<ul>\n  <li>вращающаяся башня</li>\n  <li>регулируемый угол возвышения главного орудия</li>\n  <li>подвижный пулемёт</li>\n  <li>съёмные панели корпуса и башни</li>\n  <li>детализированные внутренние секции</li>\n  <li>реалистичная конструкция гусеничной подвески</li>\n  <li>детали внешнего оборудования в задней части</li>\n</ul>\n\n<p>\nОдной из отличительных особенностей этого набора является частично доступный интерьер.\nДизайнеры воссоздали внутренние отсеки и механические детали, чтобы придать модели\nболее аутентичный и технический вид.\n</p>\n\n<h2>О настоящем танке Type 99B</h2>\n\n<p>\nType 99B — это улучшенная версия китайского основного боевого танка Type 99,\nвпервые представленного в конце 1990-х годов. Разработанная как ответ Китая на современные западные танки,\nтакие как M1 Abrams и Leopard 2, серия Type 99 представляет передовые технологии страны в области бронетехники.\n</p>\n\n<p>\nУсовершенствованный вариант Type 99B оснащён усиленной комбинированной бронёй, модернизированными системами управления огнём,\nсовременными оптическими приборами и мощным высокопроизводительным двигателем, обеспечивающим отличную мобильность на поле боя.\nЕго характерная форма башни и модульная конфигурация брони делают его одной из самых узнаваемых\nсовременных китайских бронированных машин.\n</p>\n\n<p>\nСегодня Type 99B состоит на вооружении элитных бронетанковых подразделений Народно-освободительной армии и часто\nпредставляется на военных парадах и крупных учениях как символ современных бронетанковых возможностей Китая.\n</p>\n\n<h2>Идеальный набор для поклонников военного моделизма</h2>\n\n<p>\n12GO Type 99B — идеальный выбор для:\n</p>\n\n<ul>\n  <li>коллекционеров военной техники</li>\n  <li>поклонников современной бронетехники</li>\n  <li>опытных любителей сборки конструкторов</li>\n  <li>моделистов, ориентированных на экспонирование</li>\n  <li>всех, кто ищет масштабный и увлекательный проект для сборки</li>\n</ul>\n\n<p>\nПосле завершения сборки модель станет впечатляющим центральным экспонатом благодаря своим размерам,\nреалистичным пропорциям и современному военному стилю. Это сочетание технической сборки,\nаутентичного дизайна и современных инженерных решений в области бронетехники.\n</p>",
};
